import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { STATUS_LABEL, statusCode, statusTone } from '@/lib/hr'
import type { HRAttendanceStatus } from '@/types/hr'

// Small pieces every Staff & Payroll tab uses.

export function errMsg(err: unknown): string {
  return (err as { message?: string })?.message || 'Please try again.'
}

/** A Reports-style segmented control (rounded-lg muted strip, text-sm). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  className,
}: {
  value: T
  options: { id: T; label: ReactNode; title?: string }[]
  onChange: (v: T) => void
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1', className)}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          title={o.title}
          onClick={() => onChange(o.id)}
          className={cn(
            'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            value === o.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function StatusChip({ status, leaveCode, implicit }: { status: HRAttendanceStatus | null; leaveCode?: string; implicit?: boolean }) {
  if (!status) return <span className="text-xs text-muted-foreground">Not marked</span>
  return (
    <span
      className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium', statusTone(status), implicit && 'opacity-70')}
      title={STATUS_LABEL[status]}
    >
      {status === 'leave' && leaveCode ? `${leaveCode} leave` : STATUS_LABEL[status]}
      {status !== 'leave' && <span className="sr-only"> ({statusCode(status)})</span>}
    </span>
  )
}

export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'danger' | 'warn' }) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-0.5 text-lg font-semibold tabular-nums',
          tone === 'danger' && 'text-red-700 dark:text-red-300',
          tone === 'warn' && 'text-amber-700 dark:text-amber-300',
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">{children}</div>
}

export function Banner({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'danger'; children: ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-2.5 text-sm',
        tone === 'info' && 'border-info-border bg-info-surface text-info-fg',
        tone === 'warn' && 'border-warning-border bg-warning-surface text-warning-fg',
        tone === 'danger' && 'border-danger-border bg-danger-surface text-danger-fg',
      )}
    >
      {children}
    </div>
  )
}

export function Field({ label, hint, children, className }: { label: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cn('flex flex-col gap-1.5 text-sm', className)}>
      <span className="font-medium">{label}</span>
      {children}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </label>
  )
}

/** Native select styled like the app's inputs; used where a Radix Select is overkill. */
export function NativeSelect({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        'h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50',
        className,
      )}
      {...props}
    />
  )
}
